import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, Mail, ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, login, complete2FA } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("santosh@techtreno.com");
  const [password, setPassword] = useState("Techtreno@2026");
  const [busy, setBusy] = useState(false);
  const [wallpaperUrl, setWallpaperUrl] = useState(null);

  // 2FA state
  const [twoFA, setTwoFA] = useState(null); // { temp_token, methods }
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [emailSent, setEmailSent] = useState(null);
  const [ssoConfigured, setSsoConfigured] = useState(false);

  useEffect(() => {
    api.get("/auth/microsoft/status").then(r => setSsoConfigured(!!r.data?.configured)).catch(() => {});
    api.get("/settings/login-wallpaper/meta").then(r => {
      if (r.data?.set) setWallpaperUrl(`${API}/settings/login-wallpaper?v=${r.data.version}`);
    }).catch(() => {});
    const ssoErr = params.get("sso_error");
    if (ssoErr) {
      const desc = params.get("desc") || "";
      toast.error(`Microsoft sign-in failed: ${ssoErr}${desc ? " — " + desc : ""}`);
    }
  }, [params]);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, password);
      if (data?.twofa_required) {
        setTwoFA(data);
        setMethod(data.methods.includes("totp") ? "totp" : "email");
        toast.info("Enter your two-factor code to continue");
      } else {
        toast.success("Welcome back!");
        nav("/");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await complete2FA({ temp_token: twoFA.temp_token, method, code });
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendEmailOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/email/send", { temp_token: twoFA.temp_token });
      setEmailSent(data.email_masked);
      toast.success(`OTP sent to ${data.email_masked}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const microsoftSignIn = () => {
    window.location.href = `${API}/auth/microsoft/login`;
  };

  const quickLogin = (em) => { setEmail(em); setPassword("Techtreno@2026"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:block overflow-hidden bg-[#0F172A]">
        <img
          src={wallpaperUrl || "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=1600&q=80"}
          alt="Techtreno Data Center"
          className="absolute inset-0 h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/90 via-[#0F172A]/60 to-[#DC2626]/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Techtreno" className="h-14 w-14 object-contain drop-shadow-lg" />
            <div>
              <div className="font-display font-bold text-xl leading-tight">Techtreno</div>
              <div className="text-xs text-slate-300 tracking-wider uppercase">Solutions Pvt Ltd</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Complete data-centre solutions, run by great people.
            </h1>
            <p className="mt-4 text-slate-200/90 text-base leading-relaxed">
              Techtreno delivers end-to-end Dataentre Solutions — servers, networking, storage,
              security and 24×7 uptime. This HRMS runs the humans behind that promise:
              attendance, projects, certifications and payroll, all in one place.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-200/80">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
              JWT · SSO · 2FA · Audit-ready
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/logo.png" alt="Techtreno" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-xl">Techtreno HRMS</span>
          </div>

          {!twoFA && (
            <>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
              <p className="mt-2 text-sm text-slate-500">Use your Techtreno work account to continue.</p>

              {ssoConfigured && (
                <>
                  <Button
                    type="button"
                    onClick={microsoftSignIn}
                    variant="outline"
                    className="w-full h-11 mt-6 rounded-lg font-medium border-slate-300 hover:border-[#DC2626] hover:text-[#DC2626]"
                    data-testid="ms-sso-btn"
                  >
                    <svg viewBox="0 0 21 21" className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                    </svg>
                    Sign in with Microsoft
                  </Button>
                  <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
                    <div className="h-px bg-slate-200 flex-1" />
                    <span>or with password</span>
                    <div className="h-px bg-slate-200 flex-1" />
                  </div>
                </>
              )}

              <form onSubmit={submit} className={`space-y-5 ${!ssoConfigured ? "mt-8" : ""}`} data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11" data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11" data-testid="login-password"
                  />
                </div>
                <Button
                  type="submit" disabled={busy}
                  className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] active:scale-[0.99] transition-all rounded-lg font-medium"
                  data-testid="login-submit"
                >
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Sign in
                </Button>
              </form>

              <div className="mt-8 rounded-xl border border-slate-200/70 bg-slate-50/70 p-4">
                <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500 mb-3">Quick demo access</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { l: "Admin", e: "santosh@techtreno.com" },
                    { l: "HR", e: "hr@techtreno.com" },
                    { l: "Employee", e: "employee@techtreno.com" },
                  ].map((r) => (
                    <button
                      key={r.e} type="button" onClick={() => quickLogin(r.e)}
                      className="text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#DC2626] hover:text-[#DC2626] transition-all"
                      data-testid={`demo-${r.l.toLowerCase()}`}
                    >
                      {r.l}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500 mt-3">Password: <span className="font-mono text-slate-700">Techtreno@2026</span></div>
              </div>
            </>
          )}

          {twoFA && (
            <div data-testid="twofa-panel">
              <button onClick={() => { setTwoFA(null); setCode(""); setEmailSent(null); }} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900">Two-factor verification</h2>
              <p className="mt-2 text-sm text-slate-500">This account requires an extra step.</p>

              {twoFA.methods.length > 1 && (
                <div className="mt-6 grid grid-cols-2 gap-2">
                  {twoFA.methods.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setMethod(m); setCode(""); setEmailSent(null); }}
                      className={`h-11 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-all ${method === m ? "border-[#DC2626] text-[#DC2626] bg-red-50/40" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                      data-testid={`twofa-method-${m}`}
                    >
                      {m === "totp" ? <KeyRound className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                      {m === "totp" ? "Authenticator app" : "Email OTP"}
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={verify2FA} className="mt-6 space-y-4">
                {method === "email" && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-600">
                      {emailSent ? <>Code sent to <span className="font-mono">{emailSent}</span></> : "Get a 6-digit code sent to your email."}
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={sendEmailOTP} disabled={busy} data-testid="send-email-otp">
                      {emailSent ? "Resend" : "Send code"}
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="code">6-digit code</Label>
                  <Input
                    id="code" required value={code} inputMode="numeric" maxLength={8}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="h-12 text-center tracking-[0.5em] font-mono text-lg"
                    placeholder="••••••"
                    data-testid="twofa-code"
                  />
                </div>
                <Button
                  type="submit" disabled={busy || code.length < 6}
                  className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] rounded-lg font-medium"
                  data-testid="twofa-verify"
                >
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Verify & continue
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
